import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { Link } from "react-router-dom"
import { useReactToPrint } from "react-to-print"
import * as XLSX from "xlsx"
import { ADMISSION_FEE_GROUP } from "../../../constant"
import CommonSelect, { Option } from "../../../core/common/commonSelect"
import Table from "../../../core/common/dataTable/index"
import ImageWithBasePath from "../../../core/common/imageWithBasePath"
import {
  SORT_OPTIONS,
  status,
  STUDENT_STATUS_LABELS,
} from "../../../core/common/selectoption/selectoption"
import TooltipOption from "../../../core/common/tooltipOption"
import { selectCurrentBranch } from "../../../core/data/redux/slice/branchesSlice"
import { selectCurrentSession } from "../../../core/data/redux/slice/sessionsSlice"
import { selectSystemsForCurrentBranch } from "../../../core/data/redux/slice/systemsSlice"
import { campusBrandName, campusBranchLabel } from "../../../utils/campusLogos"
import { Class, getAllClasses, getAllSections } from "../../../service/classes"
import {
  ApiResponse,
  Enrollment,
  getEnrollments,
} from "../../../service/enrollment"
import { getAllFeeGroups } from "../../../service/fee-group"
import { getAllFeesInvoices } from "../../../service/fee-invoices"
import { formatDateTime } from "../../../utils/dateutils"
import { AdmissionData } from "../../admission/admission/admission-list"
import AdmissionModals from "../../admission/admission/admissionModals"
import { all_routes } from "../../router/all_routes"
import CollectFeesModal from "../feescollection/collectFeesModal"

const CollectFees = () => {
  const routes = all_routes
  const currentSession = useSelector(selectCurrentSession)
  const systems = useSelector(selectSystemsForCurrentBranch)
  const componentRef = useRef<HTMLDivElement>(null)
  const currentBranch = useSelector(selectCurrentBranch)
  const dropdownMenuRef = useRef<HTMLDivElement | null>(null)
  const [data, setData] = useState<Enrollment[]>([])
  const [selectedEnrollment, setSelectedEnrollment] =
    useState<Enrollment | null>(null)
  const [selectedAdmission, setSelectedAdmission] =
    useState<AdmissionData | null>(null)
  const [searchText, setSearchText] = useState("")
  const [classes, setAllClasses] = useState<Option[]>([])
  const [pagination, setPagination] = useState({ limit: 10, page: 1 })
  const [paginationData, setPaginationData] = useState<any>()
  const [classesLoading, setClassesLoading] = useState(false)
  const [feeGroups, setFeeGroups] = useState<Option[]>([])
  const [sectionOptions, setSectionOptions] = useState<Option[]>([])
  const [invoices, setInvoices] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState<{
    classId?: Option
    section?: Option
    status?: Option
  } | null>(null)

  const [appliedFilters, setAppliedFilters] = useState<{
    classId?: string
    section?: string
    status?: string
    startDate?: string
    endDate?: string
    system?: string
    feeGroup?: string
  } | null>({
    system: systems[0]?._id,
    feeGroup: "all",
  })

  const [sortBy, setSortBy] = useState(SORT_OPTIONS[0])
  const isAdmission = useMemo(
    () => appliedFilters?.feeGroup === ADMISSION_FEE_GROUP,
    [appliedFilters?.feeGroup]
  )

  // Nothing here cancels an in-flight request or ignores an out-of-order
  // response -- two fetches firing close together (e.g. typing into search
  // re-triggers the effect below while an earlier fetch for a previous
  // keystroke/state is still in flight) can resolve in either order.
  // Without a guard, the LAST one to resolve always wins, even if it's the
  // stale one -- confirmed live: a correct, full search result arrived, then
  // got silently overwritten by an older/empty response finishing after it.
  // requestIdRef tags each fetchAllData() call; the result is only applied
  // if it's still the most recent one by the time it resolves.
  const requestIdRef = useRef(0)

  const fetchEnrollmentsData = async (requestId: number) => {
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        sortBy: sortBy.value.sortBy,
        sortOrder: sortBy.value.sortOrder,
        searchText,
        session: currentSession?._id,
        class: appliedFilters?.classId,
        section: appliedFilters?.section,
        system: appliedFilters?.system,
        // The dropdown carries a real admission status (Struck off, TC
        // issued, ...) — pass it through explicitly when someone picks one.
        // With nothing picked, default to the Fees scope: active, struck-off
        // and left-without-info all still belong here (money is still owed,
        // or the family may yet return to pay), only a TC'd account is
        // actually closed and drops out of this list.
        admissionStatus: appliedFilters?.status,
        statusScope: appliedFilters?.status ? undefined : ("fees" as const),
        feeGroup:
          appliedFilters?.feeGroup === "all"
            ? undefined
            : appliedFilters?.feeGroup,
        gender:
          appliedFilters?.status === "gender"
            ? appliedFilters.status
            : undefined,
      }

      const response: ApiResponse = await getEnrollments(params)
      if (requestId !== requestIdRef.current) return response.data
      setData(response.data)
      setInvoices([])
      setPaginationData(response.pagination)
      return response.data
    } catch (error) {
      console.error("Error fetching enrollments data:", error)
      return []
    }
  }

  const fetchInvoices = useCallback(async (requestId: number) => {
    try {
      const response = await getAllFeesInvoices({
        page: pagination.page,
        limit: pagination.limit,
        feeGroup: ADMISSION_FEE_GROUP,
        system: appliedFilters?.system,
        session: currentSession?._id,
        searchText,
        sortBy: sortBy.value.sortBy,
        sortOrder: sortBy.value.sortOrder,
        class: appliedFilters?.classId,
        section: appliedFilters?.section,
        status: appliedFilters?.status,
      })

      if (requestId !== requestIdRef.current) return response?.data
      setInvoices(response?.data)
      setData([])
      setPaginationData(response?.pagination)
      return response?.data
    } catch (err: any) {
      console.error("Error fetching invoices:", err)
      return []
    }
  }, [
    appliedFilters?.feeGroup,
    appliedFilters?.system,
    appliedFilters?.classId,
    appliedFilters?.section,
    appliedFilters?.status,
    currentSession?._id,
    pagination,
    searchText,
    sortBy,
    feeGroups,
  ])

  const fetchAllData = async () => {
    const requestId = ++requestIdRef.current
    setLoading(true)
    try {
      if (isAdmission) return await fetchInvoices(requestId)

      return await fetchEnrollmentsData(requestId)
    } catch (error) {
      console.error("Error fetching data:", error)
    } finally {
      if (requestId === requestIdRef.current) setLoading(false)
    }
  }

  const fetchClasses = async () => {
    setClassesLoading(true)
    try {
      const response = await getAllClasses()
      const formattedClasses = response.classes.map((classItem: Class) => ({
        label: classItem.name,
        value: classItem._id,
      }))
      setAllClasses(formattedClasses)
    } catch (error) {
      console.error("Error fetching classes:", error)
    } finally {
      setClassesLoading(false)
    }
  }

  const transformInvoiceToAdmissionData = (
    invoiceData: any
  ): AdmissionData | null => {
    if (!invoiceData) return null

    const admission = invoiceData.admission || {}

    const invoiceItems = (invoiceData.invoiceItems || []).map((item: any) => ({
      _id: item._id || Math.random().toString(36).substr(2, 9),
      feeType: item.feeType || { name: item.feeTypeName || "" },
      feeGroup: item.feeGroup || { name: item.feeGroupName || "" },
      amount: item.amount || 0,
      discount: item.discount || 0,
      description: item.description || "",
    }))

    const feeGroups =
      invoiceData.invoiceItems?.map((item: any) => ({
        _id: item.feeGroup?._id || "",
        name: item.feeGroup?.name || "",
      })) || []

    const feeTypes =
      invoiceData.invoiceItems?.map((item: any) => ({
        _id: item.feeType?._id || "",
        name: item.feeType?.name || "",
        amount: item.amount || 0,
      })) || []

    return {
      _id: admission._id || invoiceData._id,
      registrationNumber: admission.registrationNumber || "",
      temporaryGR: admission.temporaryGR || "",
      firstName: admission.firstName || "",
      lastName: admission.lastName || "",
      gender: admission.gender || "",
      status: admission.status || "",
      enrollment: admission.enrollment || "",
      createdAt:
        admission.createdAt ||
        invoiceData.createdAt ||
        new Date().toISOString(),
      dateOfBirth: admission.dateOfBirth || "",
      fatherEmail: admission.fatherEmail || "",
      fatherPhoneNumber: admission.fatherPhoneNumber || "",
      fatherName: admission.fatherName || "",
      studentPicture:
        admission.profilePicture || admission.studentPicture || "",
      appliedClass: admission.appliedClass || { name: "" },
      section: admission.section || { name: "", _id: "" },
      feeGroups: feeGroups,
      feeTypes: feeTypes,
      invoice: {
        _id: invoiceData._id || "",
        discount: invoiceData.discount || 0,
        status: invoiceData.status || "unpaid",
        invoiceNumber: invoiceData.invoiceNumber || "",
        total: invoiceData.total || 0,
        subTotal: invoiceData.subTotal || invoiceData.total || 0,
        paymentType: invoiceData.paymentType || "cash",
        dueDate: invoiceData.dueDate || null,
        invoiceItems: invoiceItems,
        notes: invoiceData.notes || "",
      },
    }
  }

  const fetchSections = async () => {
    try {
      const data = await getAllSections({ session: currentSession?._id })
      const formattedSections = data.sections.map((section: any) => ({
        label: section.name,
        value: section._id,
      }))
      setSectionOptions(formattedSections)
    } catch (error) {
      console.error("Failed to fetch sections:", error)
    }
  }

  const fetchFeeGroups = async () => {
    try {
      const response = await getAllFeeGroups({
        session: currentSession?._id,
        system: appliedFilters?.system,
      })

      const allFeesOption = {
        _id: "all",
        name: "All Fees",
        label: "All Fees",
        value: "all",
      }
      const formattedFeeGroups = [allFeesOption, ...response.data]

      setFeeGroups(formattedFeeGroups)

      if (!appliedFilters?.feeGroup) {
        setAppliedFilters(prev => ({
          ...prev,
          feeGroup: "all",
        }))
      }
    } catch (error) {
      console.error("Error fetching fee groups:", error)
    }
  }

  // `appliedFilters.system` starts from `systems[0]?._id` in useState's
  // initializer, which only runs once at mount -- `systems` (Redux) is
  // almost always still empty at that exact instant, since it loads from
  // the server a moment later. With no other fallback, `appliedFilters.system`
  // was left permanently unset, and everything below is gated on it being
  // truthy -- the whole fee list silently never loaded. Only happened to
  // recover when something else (switching campus) forced a fresh mount.
  // This backfills it the moment `systems` actually arrives, for every user
  // regardless of how many campuses they have. Only ever fires once per
  // session: once `appliedFilters.system` is set (by this, by the System
  // dropdown, or by Reset Filters), the guard below keeps it from firing
  // again.
  useEffect(() => {
    if (systems.length > 0 && !appliedFilters?.system) {
      setAppliedFilters(prev => ({ ...prev, system: systems[0]._id }))
    }
  }, [systems])

  useEffect(() => {
    fetchFeeGroups()
  }, [currentSession?._id, appliedFilters?.system])

  useEffect(() => {
    fetchSections()
  }, [currentSession?._id])

  useEffect(() => {
    if (appliedFilters?.system) {
      fetchAllData()
    }
  }, [pagination, currentSession?._id, appliedFilters, searchText, sortBy])

  useEffect(() => {
    fetchClasses()
  }, [])

  const handleChange = (page: number, pageSize: number) => {
    setPagination({ limit: pageSize, page: page })
  }

  const handleApplyClick = () => {
    if (dropdownMenuRef.current) {
      dropdownMenuRef.current.classList.remove("show")
      const toggle = dropdownMenuRef.current
        .closest(".dropdown")
        ?.querySelector(".dropdown-toggle") as HTMLElement | null
      if (toggle) {
        toggle.classList.remove("show")
        toggle.setAttribute("aria-expanded", "false")
      }
    }
    // Reset to first page when filters change
    setPagination(prev => ({ ...prev, page: 1 }))
    setAppliedFilters({
      ...appliedFilters,
      classId: filters?.classId?.value as string,
      section: filters?.section?.value as string,
      status: filters?.status?.value as string,
    })
  }

  const handlePrint = useReactToPrint({ contentRef: componentRef })

  const handleExportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(isAdmission ? invoices : data)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, "FeesData")
    XLSX.writeFile(wb, "fees_export.xlsx")
  }

  const handleSortSelect = (item: {
    label: string
    value: {
      sortBy: string
      sortOrder: string
    }
  }) => {
    setSortBy(item)
  }

  const handleResetFilters = () => {
    setFilters({
      classId: {
        label: "Select",
        value: "",
      },
      section: {
        label: "Select",
        value: "",
      },
      status: {
        label: "Select",
        value: "",
      },
    })
    // Reset to first page when filters are reset
    setPagination(prev => ({ ...prev, page: 1 }))
    setAppliedFilters({
      system: systems[0]?._id,
      feeGroup: "all",
      classId: undefined,
      section: undefined,
      status: undefined,
    })
  }

  const combinedColumns = [
    {
      title: isAdmission ? "Temporary GR" : "GR No",
      dataIndex: "grNumber",
      width: 120,
      render: (_text: string, record: any) => {
        if (!isAdmission) {
          const grNumber = record?.student?.grNumber || "N/A"
          const enrollmentId = record?._id
          if (enrollmentId) {
            return (
              <Link
                to={`/student/student-details/${enrollmentId}`}
                className="link-primary"
              >
                {grNumber}
              </Link>
            )
          }
          return grNumber
        }
        return record?.admission?.temporaryGR || "N/A"
      },
    },
    {
      title: "Name",
      dataIndex: "name",
      render: (_text: string, record: any) => {
        if (!isAdmission) {
          const enrollmentId = record?._id
          return (
            <div className="d-flex align-items-center">
              <Link to="#" className="avatar avatar-md">
                <ImageWithBasePath
                  src={"assets/img/students/student-01.jpg"}
                  fullSrc={record?.user?.profilePicture}
                  className="img-fluid rounded-circle"
                  alt="img"
                />
              </Link>
              <div className="ms-2">
                <p className="text-dark mb-0">
                  <Link
                    to={enrollmentId ? `/student/student-details/${enrollmentId}` : "#"}
                  >
                    {record?.user?.firstName} {record?.user?.lastName}
                  </Link>
                </p>
              </div>
            </div>
          )
        }
        return (
          <div className="d-flex align-items-center">
            <Link to="#" className="avatar avatar-md">
              <ImageWithBasePath
                src={"assets/img/students/student-01.jpg"}
                fullSrc={record?.admission?.profilePicture}
                className="img-fluid rounded-circle"
                alt="img"
              />
            </Link>
            <div className="ms-2">
              <p className="text-dark mb-0">
                <Link to={routes.studentDetail} state={record}>
                  {record?.admission?.firstName} {record?.admission?.lastName}
                </Link>
              </p>
            </div>
          </div>
        )
      },
    },
    {
      title: "Class",
      dataIndex: "className",
      width: 120,
      render: (_text: string, record: any) => {
        if (!isAdmission) {
          return record?.class?.name || "N/A"
        }

        return record?.admission?.appliedClass?.name || "N/A"
      },
    },
    {
      title: "Section",
      dataIndex: "sectionName",
      width: 120,
      render: (_text: string, record: any) => {
        if (!isAdmission) {
          return record?.section?.name || "N/A"
        }
        return record?.admission?.section?.name || "N/A"
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 130,
      render: (_text: string, record: any) => {
        if (!isAdmission) {
          // Standing comes from Admission (via admission or student.admission),
          // falling back to Enrollment's own status flag if admission is absent.
          // Struck-off students maintain an active enrolment flag so they stay reachable
          // in fee collection and head count, but their true standing is "struck_off".
          const admissionStatus =
            record?.admission?.status ||
            record?.student?.admission?.status
          const rawStatus = admissionStatus || record?.status || "active"
          const label =
            STUDENT_STATUS_LABELS[rawStatus] ||
            (rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1))

          const badgeClass =
            rawStatus === "admitted" || rawStatus === "active"
              ? "badge-soft-success"
              : rawStatus === "struck_off"
              ? "badge-soft-danger"
              : "badge-soft-warning"

          return (
            <span
              className={`badge d-inline-flex align-items-center ${badgeClass}`}
            >
              <i className="ti ti-circle-filled fs-5 me-1"></i>
              {label}
            </span>
          )
        }

        const invoiceStatus = record?.status || "N/A"
        const statusClass =
          invoiceStatus === "paid" ? "badge-soft-success" : "badge-soft-danger"

        return (
          <span className={`badge ${statusClass}`}>
            {invoiceStatus?.charAt(0).toUpperCase() + invoiceStatus?.slice(1)}
          </span>
        )
      },
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      width: 150,
      render: (createdAt: string) => formatDateTime(createdAt),
    },
{
  title: "Action",
  dataIndex: "action",
  width: 150,
  render: (_status: string, record: any) => {

    if (!isAdmission) {
      return (
        <div className="d-flex align-items-center">
          <Link
            to="#"
            className="btn btn-light me-2"
            data-bs-toggle="modal"
            data-bs-target="#create_fee_modal"
            onClick={() => setSelectedEnrollment(record)}
          >
            Collect Fees
          </Link>
        </div>
      )
    }

    const invoiceStatus = record?.status?.toLowerCase() || "unpaid"
    const isPaid = invoiceStatus === "paid"

    if (isPaid) {
      return (
        <div className="d-flex align-items-center">
          <Link
            to="#"
            className="btn btn-primary me-2"
            data-bs-toggle="modal"
            data-bs-target="#add_fees_collect"
            onClick={() => {
              const admissionData = transformInvoiceToAdmissionData(record)
              setSelectedAdmission(admissionData)
            }}
          >
            Print
          </Link>
        </div>
      )
    } else {
      return (
        <div className="d-flex align-items-center">
          <Link
            to="#"
            className="btn btn-light me-2"
            data-bs-toggle="modal"
            data-bs-target="#add_fees_collect"
            onClick={() => {
              const admissionData = transformInvoiceToAdmissionData(record)
              setSelectedAdmission(admissionData)
            }}
          >
            Collect Fee
          </Link>
        </div>
      )
    }
  },
}
  ]

  const onFeeModalClose = () => {
    setSelectedEnrollment(null)
  }

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Fees Collection</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="#">Fees Collection</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Collect Fees
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <TooltipOption
                onRefresh={fetchAllData}
                onExportPdf={handlePrint}
                onExportExcel={handleExportExcel}
              />
            </div>
          </div>

          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Fees List</h4>
              <div className="d-flex align-items-center flex-wrap">
                <div className="mb-3 me-2 fee-group-select">
                  <CommonSelect
                    className="select"
                    options={feeGroups}
                    value={
                      feeGroups.find(
                        item => item._id === (appliedFilters?.feeGroup || "all")
                      ) || { _id: "all", name: "All Fees" }
                    }
                    getOptionValue={item => item._id}
                    getOptionLabel={item => item.name}
                    onChange={feeGroupItem => {
                      setAppliedFilters(prev => ({
                        ...prev,
                        feeGroup: feeGroupItem?._id || "all",
                      }))
                    }}
                  />
                </div>
                <div className="mb-3 me-2">
                  <CommonSelect
                    className="select"
                    options={systems}
                    value={systems.find(
                      item => item._id === appliedFilters?.system
                    )}
                    getOptionValue={item => item._id}
                    getOptionLabel={item => item.name}
                    onChange={systemItem =>
                      setAppliedFilters(prev => ({
                        ...prev,
                        system: systemItem?._id || "",
                      }))
                    }
                  />
                </div>
                <div className="dropdown mb-3 me-2">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                    data-bs-auto-close="outside"
                  >
                    <i className="ti ti-filter me-2" />
                    Filter
                  </Link>
                  <div
                    className="dropdown-menu drop-width"
                    ref={dropdownMenuRef}
                    onClick={event => event.stopPropagation()}
                  >
                    <form>
                      <div className="d-flex align-items-center border-bottom p-3">
                        <h4>Filter</h4>
                      </div>
                      <div className="p-3 pb-0 border-bottom">
                        <div className="row">
                          <div className="col-md-12">
                            <div className="mb-3">
                              <label className="form-label">Class</label>
                              {classesLoading ? (
                                <div> Loading classes... </div>
                              ) : (
                                <CommonSelect
                                  className="select"
                                  options={classes}
                                  value={filters?.classId}
                                  menuPortalTarget={null}
                                  styles={{
                                    menu: (base: any) => ({ ...base, zIndex: 9999 }),
                                  }}
                                  onChange={classItem =>
                                    setFilters(prev => ({
                                      ...prev,
                                      classId: classItem as Option,
                                    }))
                                  }
                                />
                              )}
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">Section</label>
                              <CommonSelect
                                className="select"
                                options={sectionOptions}
                                value={filters?.section}
                                menuPortalTarget={null}
                                styles={{
                                  menu: (base: any) => ({ ...base, zIndex: 9999 }),
                                }}
                                onChange={sectionItem =>
                                  setFilters(prev => ({
                                    ...prev,
                                    section: sectionItem as Option,
                                  }))
                                }
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">Fee Status</label>
                              <CommonSelect
                                className="select"
                                options={status}
                                value={filters?.status}
                                menuPortalTarget={null}
                                styles={{
                                  menu: (base: any) => ({ ...base, zIndex: 9999 }),
                                }}
                                onChange={statusItem =>
                                  setFilters(prev => ({
                                    ...prev,
                                    status: statusItem as Option,
                                  }))
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="p-3 d-flex align-items-center justify-content-end">
                        <Link
                          to="#"
                          className="btn btn-light me-3"
                          onClick={handleResetFilters}
                        >
                          Reset
                        </Link>
                        <div
                          className="btn btn-primary"
                          onClick={handleApplyClick}
                        >
                          Apply
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
                <div className="dropdown mb-3">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                  >
                    <i className="ti ti-sort-ascending-2 me-2" />
                    Sort by {sortBy.label}
                  </Link>
                  <ul className="dropdown-menu p-3">
                    {SORT_OPTIONS.map((item, index) => (
                      <li key={index}>
                        <div
                          className={`dropdown-item rounded-1 ${
                            item.label === sortBy.label ? "active" : ""
                          }`}
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSortSelect(item)}
                        >
                          {item.label}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            <div className="card-body p-0 py-3 printable-content" ref={componentRef}>
              <div className="print-only-header">
                <div className="school-brand">
                  <img src={currentBranch?.blackLogoUrl || "/assets/img/icons/logo.png"} alt={campusBrandName(currentBranch)} />
                  <strong>{campusBrandName(currentBranch)}{campusBranchLabel(currentBranch) ? ` - ${campusBranchLabel(currentBranch)}` : ""}</strong>
                </div>
                <div className="report-title">Fees List</div>
                <div className="print-date">{new Date().toLocaleDateString("en-GB")}</div>
              </div>
              <Table
                setSearchText={setSearchText}
                dataSource={isAdmission ? invoices : data}
                columns={combinedColumns}
                loading={loading}
                pagination={{ ...paginationData, onChange: handleChange }}
              />
            </div>
          </div>
        </div>
      </div>
      <CollectFeesModal
        onClose={onFeeModalClose}
        studentData={selectedEnrollment}
        selectedSystem={appliedFilters?.system}
        feeGroup={appliedFilters?.feeGroup}
      />

      <AdmissionModals
        selectedId={selectedAdmission?._id}
        setSelectedId={id => setSelectedAdmission(null)}
        fetchData={fetchAllData}
        admissionData={selectedAdmission}
      />
    </>
  )
}

export default CollectFees
